import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { api } from '../services/api'
import { AlertCircle, Eye, EyeOff, GraduationCap, Users } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'

type Role = 'student' | 'parent'

export default function RegisterPage() {
  const [role, setRole] = useState<Role>('student')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const register = useAuthStore((state) => state.register)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password !== confirmPassword) { setError('Las contraseñas no coinciden'); return }
    if (password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres'); return }
    setIsLoading(true)
    try {
      await register(email, password, name, role)

      // Consume pending join code (from deep link or query param) — solo para estudiantes
      if (role === 'student') {
        const joinCode = searchParams.get('joinCode') || sessionStorage.getItem('pendingLinkCode')
        if (joinCode) {
          try {
            await api.useLinkCode(joinCode)
          } catch {
            // Non-critical — code may be expired; user can use it manually later
          }
          sessionStorage.removeItem('pendingLinkCode')
        }
      }

      navigate(role === 'parent' ? '/parent' : '/')
    } catch {
      setError('Error al crear la cuenta. Por favor intenta de nuevo.')
    } finally {
      setIsLoading(false)
    }
  }

  const inputStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#E2E8F0',
  }
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) =>
    (e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)')
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) =>
    (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'var(--color-bg)' }}
    >
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(79,70,229,0.15) 0%, transparent 70%)',
        }}
      />

      <div className="w-full max-w-[400px] relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-4"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)' }}
          >
            <span className="text-white font-bold text-lg">K</span>
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>kapri</h1>
          <p className="text-sm mt-1" style={{ color: '#64748B' }}>
            Crea tu cuenta y empieza a aprender
          </p>
        </div>

        <div className="glass-card p-8">
          <h2 className="text-xl font-semibold mb-5" style={{ color: '#E2E8F0' }}>
            Crear cuenta
          </h2>

          {/* Role selector */}
          <div className="grid grid-cols-2 gap-2 mb-6">
            {([
              { value: 'student', label: 'Soy estudiante', Icon: GraduationCap },
              { value: 'parent',  label: 'Soy padre/tutor', Icon: Users },
            ] as const).map(({ value, label, Icon }) => {
              const active = role === value
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRole(value)}
                  className="flex flex-col items-center gap-2 py-3 px-2 rounded-xl text-xs font-medium transition-all"
                  style={{
                    background: active ? 'rgba(79,70,229,0.15)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${active ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.08)'}`,
                    color: active ? '#A5B4FC' : '#64748B',
                  }}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </button>
              )
            })}
          </div>

          {error && (
            <div
              className="flex items-start gap-2.5 p-3.5 rounded-xl mb-5 text-sm"
              style={{
                background: 'rgba(244,63,94,0.08)',
                border: '1px solid rgba(244,63,94,0.2)',
                color: '#FB7185',
              }}
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {[
              { label: 'Nombre completo', id: 'name', type: 'text', value: name, onChange: setName, placeholder: 'Juan García', autoComplete: 'name' },
              { label: 'Email', id: 'email', type: 'email', value: email, onChange: setEmail, placeholder: 'tu@email.com', autoComplete: 'email' },
            ].map(({ label, id, type, value, onChange, placeholder, autoComplete }) => (
              <div key={id}>
                <label className="block text-sm font-medium mb-1.5" style={{ color: '#94A3B8' }}>
                  {label}
                </label>
                <input
                  id={id}
                  type={type}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  required
                  autoComplete={autoComplete}
                  placeholder={placeholder}
                  className="w-full px-4 py-2.5 rounded-xl text-sm outline-none transition-all"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </div>
            ))}

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#94A3B8' }}>
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-4 py-2.5 pr-10 rounded-xl text-sm outline-none transition-all"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: '#64748B' }}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#94A3B8' }}>
                Confirmar contraseña
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="••••••••"
                className="w-full px-4 py-2.5 rounded-xl text-sm outline-none transition-all"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : 'Crear cuenta'}
            </button>
          </form>

          <p className="mt-6 text-sm text-center" style={{ color: '#64748B' }}>
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="font-medium" style={{ color: '#818CF8' }}>
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
